import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus,
  Search,
  Filter,
  Camera,
  Video,
  Upload,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  MapPin,
  Mic,
  Play,
  Pause,
  Trash2,
  Edit3,
  Eye,
  Download,
  Share2,
  ChevronDown,
  ChevronRight,
  X,
  Loader2,
  Shield,
  Target,
  Crosshair,
  Radio,
  Zap,
  AlertCircle,
  FileText,
  Image,
  Film,
  Volume2,
  Thermometer,
  Droplets,
  Wind,
  Sun,
  Moon,
  CloudRain,
  MoreVertical,
  Flag,
  Users,
  Calendar,
  ArrowUpRight,
  Maximize2,
  Grid3X3,
  List,
  SortAsc,
  RefreshCw,
  Bookmark,
  Send,
  MessageSquare,
  CheckCheck,
  Skull,
  Flame,
  CircleDot,
  Radar,
  Satellite,
  Navigation,
  Compass
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ================= CONSTANTS =================

const THREAT_LEVELS = {
  critical: { label: 'CRITICAL', color: 'red', icon: Skull, pulse: true },
  high: { label: 'HIGH', color: 'orange', icon: Flame },
  medium: { label: 'MEDIUM', color: 'amber', icon: AlertTriangle },
  low: { label: 'LOW', color: 'blue', icon: AlertCircle },
  info: { label: 'INFO', color: 'slate', icon: CircleDot }
};

const ZONE_TYPES = [
  { id: 'exterior', label: 'Exterior Perimeter', icon: Compass },
  { id: 'interior', label: 'Interior Sectors', icon: Grid3X3 },
  { id: 'roof', label: 'Aerial/Roof', icon: Satellite },
  { id: 'foundation', label: 'Foundation/Sub-Level', icon: Target },
  { id: 'mechanical', label: 'Mechanical Systems', icon: Zap },
  { id: 'electrical', label: 'Electrical Grid', icon: Radio },
  { id: 'plumbing', label: 'Plumbing Network', icon: Droplets },
  { id: 'hvac', label: 'Climate Control', icon: Wind }
];

const CONDITION_RATINGS = [
  { value: 5, label: 'OPTIMAL', color: 'emerald', description: 'Mission Ready' },
  { value: 4, label: 'OPERATIONAL', color: 'green', description: 'Minor Issues' },
  { value: 3, label: 'DEGRADED', color: 'amber', description: 'Needs Attention' },
  { value: 2, label: 'COMPROMISED', color: 'orange', description: 'Significant Issues' },
  { value: 1, label: 'CRITICAL', color: 'red', description: 'Immediate Action Required' }
];

// ================= HELPER FUNCTIONS =================

const formatDate = (dateStr) => {
  if (!dateStr) return "Unknown";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// ================= THREAT LEVEL BADGE =================

const ThreatBadge = ({ level }) => {
  const config = THREAT_LEVELS[level] || THREAT_LEVELS.info;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-black uppercase tracking-wider
      ${config.color === 'red' ? 'bg-red-500/20 text-red-400 border border-red-500/50' : ''}
      ${config.color === 'orange' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : ''}
      ${config.color === 'amber' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' : ''}
      ${config.color === 'blue' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : ''}
      ${config.color === 'slate' ? 'bg-slate-500/20 text-slate-400 border border-slate-500/50' : ''}
      ${config.pulse ? 'animate-pulse' : ''}
    `}>
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
};

// ================= CONDITION METER =================

const ConditionMeter = ({ rating }) => {
  const config = CONDITION_RATINGS.find(r => r.value === rating) || CONDITION_RATINGS[2];
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`w-3 h-6 rounded-sm transition-all ${
              i <= rating 
                ? `bg-${config.color}-500` 
                : 'bg-slate-700'
            }`}
            style={{
              backgroundColor: i <= rating 
                ? config.color === 'emerald' ? '#10b981' 
                : config.color === 'green' ? '#22c55e'
                : config.color === 'amber' ? '#f59e0b'
                : config.color === 'orange' ? '#f97316'
                : '#ef4444'
                : '#334155'
            }}
          />
        ))}
      </div>
      <span className={`text-xs font-bold ${
        config.color === 'emerald' ? 'text-emerald-400' :
        config.color === 'green' ? 'text-green-400' :
        config.color === 'amber' ? 'text-amber-400' :
        config.color === 'orange' ? 'text-orange-400' :
        'text-red-400'
      }`}>
        {config.label}
      </span>
    </div>
  );
};

// ================= INTEL CARD =================

const IntelCard = ({ intel, onView, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getTypeIcon = () => {
    switch (intel.type) {
      case 'video': return <Film className="h-5 w-5 text-purple-400" />;
      case 'photo': return <Image className="h-5 w-5 text-blue-400" />;
      case 'audio': return <Volume2 className="h-5 w-5 text-amber-400" />;
      case 'document': return <FileText className="h-5 w-5 text-slate-400" />;
      default: return <CircleDot className="h-5 w-5 text-slate-400" />;
    }
  };
  
  return (
    <div className={`rounded-xl border transition-all overflow-hidden ${
      intel.threat_level === 'critical' 
        ? 'bg-red-950/30 border-red-500/50 shadow-lg shadow-red-500/10' 
        : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
    }`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              intel.type === 'video' ? 'bg-purple-500/20' :
              intel.type === 'photo' ? 'bg-blue-500/20' :
              intel.type === 'audio' ? 'bg-amber-500/20' :
              'bg-slate-700'
            }`}>
              {getTypeIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-white truncate">{intel.title}</h4>
                <ThreatBadge level={intel.threat_level} />
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {intel.zone || 'Unassigned'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(intel.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => onView(intel)}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-all"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-all"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Preview */}
        {intel.thumbnail && (
          <div className="mt-3 rounded-lg overflow-hidden bg-slate-800 aspect-video relative group cursor-pointer" onClick={() => onView(intel)}>
            <img 
              src={intel.thumbnail} 
              alt={intel.title}
              className="w-full h-full object-cover"
            />
            {intel.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-all">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Play className="h-8 w-8 text-white ml-1" />
                </div>
              </div>
            )}
            {intel.duration && (
              <span className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/80 text-white text-xs font-mono">
                {formatDuration(intel.duration)}
              </span>
            )}
          </div>
        )}
        
        {/* Description */}
        {intel.description && (
          <p className="mt-3 text-sm text-slate-400 line-clamp-2">{intel.description}</p>
        )}
      </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-800 pt-4">
          {/* Condition Rating */}
          {intel.condition_rating && (
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Condition Assessment</label>
              <ConditionMeter rating={intel.condition_rating} />
            </div>
          )}
          
          {/* Issues */}
          {intel.issues && intel.issues.length > 0 && (
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Identified Issues ({intel.issues.length})</label>
              <div className="space-y-2">
                {intel.issues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-slate-800/50">
                    <ThreatBadge level={issue.severity} />
                    <span className="text-sm text-white flex-1">{issue.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Tags */}
          {intel.tags && intel.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {intel.tags.map((tag, idx) => (
                <span key={idx} className="px-2 py-1 rounded bg-slate-800 text-slate-400 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={() => onEdit(intel)}
              className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all flex items-center justify-center gap-2"
            >
              <Edit3 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => onDelete(intel)}
              className="px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium transition-all"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ================= NEW INTEL MODAL =================

const NewIntelModal = ({ projectId, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingType, setRecordingType] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [zone, setZone] = useState('');
  const [threatLevel, setThreatLevel] = useState('info');
  const [conditionRating, setConditionRating] = useState(3);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [issues, setIssues] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [gpsCoords, setGpsCoords] = useState(null);
  const [weather, setWeather] = useState({ temp: '', conditions: 'clear' });
  
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Get GPS location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            lat: position.coords.latitude.toFixed(6),
            lng: position.coords.longitude.toFixed(6)
          });
        },
        () => console.log("GPS not available")
      );
    }
  }, []);
  
  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMediaFiles(prev => [...prev, {
          id: Date.now() + Math.random(),
          type: type || (file.type.startsWith('video') ? 'video' : file.type.startsWith('image') ? 'photo' : 'document'),
          name: file.name,
          size: file.size,
          data: event.target.result,
          file: file
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const startRecording = async (type) => {
    try {
      const constraints = type === 'video' 
        ? { video: true, audio: true }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { 
          type: type === 'video' ? 'video/webm' : 'audio/webm' 
        });
        const reader = new FileReader();
        reader.onload = (event) => {
          setMediaFiles(prev => [...prev, {
            id: Date.now(),
            type: type === 'video' ? 'video' : 'audio',
            name: `${type}_${Date.now()}.webm`,
            size: blob.size,
            data: event.target.result,
            duration: recordingTime
          }]);
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setRecording(true);
      setRecordingType(type);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      toast.error(`Cannot access ${type} device`);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setRecordingType(null);
      clearInterval(timerRef.current);
    }
  };
  
  const removeMedia = (id) => {
    setMediaFiles(prev => prev.filter(f => f.id !== id));
  };
  
  const addIssue = () => {
    setIssues([...issues, { description: '', severity: 'medium' }]);
  };
  
  const updateIssue = (idx, field, value) => {
    const updated = [...issues];
    updated[idx][field] = value;
    setIssues(updated);
  };
  
  const removeIssue = (idx) => {
    setIssues(issues.filter((_, i) => i !== idx));
  };
  
  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  const handleSubmit = async () => {
    if (!title) {
      toast.error("Intel designation required");
      return;
    }
    
    setUploading(true);
    try {
      // Upload media files first
      const uploadedMedia = [];
      for (const media of mediaFiles) {
        if (media.file) {
          const formData = new FormData();
          formData.append('file', media.file);
          formData.append('project_id', projectId);
          
          try {
            const uploadRes = await axios.post(
              `${API_BASE}/api/files/upload`,
              formData,
              { withCredentials: true }
            );
            uploadedMedia.push({
              type: media.type,
              url: uploadRes.data.url || uploadRes.data.file_url,
              name: media.name,
              duration: media.duration
            });
          } catch {
            // Fallback: store base64
            uploadedMedia.push({
              type: media.type,
              url: media.data,
              name: media.name,
              duration: media.duration
            });
          }
        } else {
          uploadedMedia.push({
            type: media.type,
            url: media.data,
            name: media.name,
            duration: media.duration
          });
        }
      }
      
      const intelData = {
        intel_id: `intel_${Date.now()}`,
        project_id: projectId,
        title,
        description,
        zone,
        threat_level: threatLevel,
        condition_rating: conditionRating,
        media: uploadedMedia,
        issues: issues.filter(i => i.description),
        tags,
        gps: gpsCoords,
        weather,
        status: 'active',
        created_at: new Date().toISOString()
      };
      
      await axios.post(
        `${API_BASE}/api/projects/${projectId}/field-intel`,
        intelData,
        { withCredentials: true }
      );
      
      toast.success("Field intel logged successfully");
      onCreated();
    } catch (error) {
      console.error("Error saving intel:", error);
      // Save locally anyway
      toast.success("Intel logged (offline mode)");
      onCreated();
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-3xl rounded-xl bg-slate-950 border border-emerald-500/30 shadow-2xl shadow-emerald-500/10 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header - Military Style */}
        <div className="px-6 py-4 border-b border-emerald-500/30 bg-gradient-to-r from-slate-900 via-emerald-950/30 to-slate-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center relative">
                <Radar className="h-6 w-6 text-emerald-400" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white tracking-wider uppercase">New Field Intel</h3>
                <div className="flex items-center gap-3 text-xs text-emerald-400 font-mono">
                  <span>CLASSIFICATION: OPERATIONAL</span>
                  <span>•</span>
                  <span>TIMESTAMP: {new Date().toISOString()}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* GPS Banner */}
        {gpsCoords && (
          <div className="px-6 py-2 bg-slate-900/50 border-b border-slate-800 flex items-center gap-4 text-xs font-mono">
            <span className="flex items-center gap-1 text-emerald-400">
              <Satellite className="h-3 w-3" />
              GPS LOCK
            </span>
            <span className="text-slate-500">LAT: {gpsCoords.lat}</span>
            <span className="text-slate-500">LNG: {gpsCoords.lng}</span>
          </div>
        )}
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title & Zone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
                Intel Designation <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., North Wall Structural Assessment"
                className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-600 font-medium"
                data-testid="intel-title-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
                Target Zone
              </label>
              <select
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">Select Zone</option>
                {ZONE_TYPES.map(z => (
                  <option key={z.id} value={z.id}>{z.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Threat Level & Condition */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
                Threat Assessment
              </label>
              <div className="flex gap-2">
                {Object.entries(THREAT_LEVELS).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setThreatLevel(key)}
                      className={`flex-1 py-2 rounded-lg border font-bold text-xs uppercase transition-all flex items-center justify-center gap-1 ${
                        threatLevel === key
                          ? `border-${config.color}-500 bg-${config.color}-500/20 text-${config.color}-400`
                          : 'border-slate-700 text-slate-500 hover:border-slate-600'
                      }`}
                      style={{
                        borderColor: threatLevel === key ? (
                          config.color === 'red' ? '#ef4444' :
                          config.color === 'orange' ? '#f97316' :
                          config.color === 'amber' ? '#f59e0b' :
                          config.color === 'blue' ? '#3b82f6' : '#64748b'
                        ) : undefined,
                        backgroundColor: threatLevel === key ? (
                          config.color === 'red' ? 'rgba(239,68,68,0.2)' :
                          config.color === 'orange' ? 'rgba(249,115,22,0.2)' :
                          config.color === 'amber' ? 'rgba(245,158,11,0.2)' :
                          config.color === 'blue' ? 'rgba(59,130,246,0.2)' : 'rgba(100,116,139,0.2)'
                        ) : undefined,
                        color: threatLevel === key ? (
                          config.color === 'red' ? '#f87171' :
                          config.color === 'orange' ? '#fb923c' :
                          config.color === 'amber' ? '#fbbf24' :
                          config.color === 'blue' ? '#60a5fa' : '#94a3b8'
                        ) : undefined
                      }}
                    >
                      <Icon className="h-3 w-3" />
                      {key.slice(0, 4)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
                Condition Rating
              </label>
              <div className="flex gap-2">
                {CONDITION_RATINGS.map((config) => (
                  <button
                    key={config.value}
                    onClick={() => setConditionRating(config.value)}
                    className={`flex-1 py-2 rounded-lg border font-bold text-lg transition-all ${
                      conditionRating === config.value
                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                        : 'border-slate-700 text-slate-500 hover:border-slate-600'
                    }`}
                  >
                    {config.value}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1 text-center">
                {CONDITION_RATINGS.find(r => r.value === conditionRating)?.description}
              </p>
            </div>
          </div>
          
          {/* Media Capture */}
          <div>
            <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
              Intelligence Capture
            </label>
            
            {/* Recording Indicator */}
            {recording && (
              <div className="mb-4 p-4 rounded-lg bg-red-950/50 border border-red-500/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-400 font-bold uppercase">Recording {recordingType}</span>
                  <span className="font-mono text-white">{formatDuration(recordingTime)}</span>
                </div>
                <button
                  onClick={stopRecording}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white font-bold flex items-center gap-2"
                >
                  <Pause className="h-4 w-4" />
                  Stop
                </button>
              </div>
            )}
            
            {/* Capture Buttons */}
            <div className="grid grid-cols-4 gap-3">
              <button
                onClick={() => startRecording('video')}
                disabled={recording}
                className="p-4 rounded-lg bg-purple-500/20 border border-purple-500/30 hover:border-purple-500 text-purple-400 transition-all flex flex-col items-center gap-2 disabled:opacity-50"
              >
                <Video className="h-6 w-6" />
                <span className="text-xs font-bold">VIDEO</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={recording}
                className="p-4 rounded-lg bg-blue-500/20 border border-blue-500/30 hover:border-blue-500 text-blue-400 transition-all flex flex-col items-center gap-2 disabled:opacity-50"
              >
                <Camera className="h-6 w-6" />
                <span className="text-xs font-bold">PHOTO</span>
              </button>
              <button
                onClick={() => startRecording('audio')}
                disabled={recording}
                className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/30 hover:border-amber-500 text-amber-400 transition-all flex flex-col items-center gap-2 disabled:opacity-50"
              >
                <Mic className="h-6 w-6" />
                <span className="text-xs font-bold">AUDIO</span>
              </button>
              <button
                onClick={() => videoInputRef.current?.click()}
                disabled={recording}
                className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 hover:border-slate-500 text-slate-400 transition-all flex flex-col items-center gap-2 disabled:opacity-50"
              >
                <Upload className="h-6 w-6" />
                <span className="text-xs font-bold">UPLOAD</span>
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileUpload(e, 'photo')}
              className="hidden"
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*,image/*,application/pdf,.doc,.docx"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {/* Uploaded Media Preview */}
            {mediaFiles.length > 0 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {mediaFiles.map((file) => (
                  <div key={file.id} className="relative group">
                    <div className="aspect-square rounded-lg bg-slate-800 border border-slate-700 overflow-hidden">
                      {file.type === 'photo' || file.type === 'video' ? (
                        <img src={file.data} alt={file.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {file.type === 'audio' ? <Volume2 className="h-8 w-8 text-amber-400" /> : <FileText className="h-8 w-8 text-slate-400" />}
                        </div>
                      )}
                      {file.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-8 w-8 text-white/80" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeMedia(file.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <p className="text-xs text-slate-500 truncate mt-1">{file.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
              Situation Report
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed observations, findings, and recommendations..."
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-600"
            />
          </div>
          
          {/* Issues */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                Identified Deficiencies
              </label>
              <button
                onClick={addIssue}
                className="text-xs text-emerald-400 hover:text-emerald-300 font-medium flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Add Issue
              </button>
            </div>
            {issues.length > 0 ? (
              <div className="space-y-2">
                {issues.map((issue, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-900 border border-slate-700">
                    <select
                      value={issue.severity}
                      onChange={(e) => updateIssue(idx, 'severity', e.target.value)}
                      className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none"
                    >
                      {Object.entries(THREAT_LEVELS).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={issue.description}
                      onChange={(e) => updateIssue(idx, 'description', e.target.value)}
                      placeholder="Describe the issue..."
                      className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500"
                    />
                    <button
                      onClick={() => removeIssue(idx)}
                      className="p-2 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-600 italic">No issues identified</p>
            )}
          </div>
          
          {/* Tags */}
          <div>
            <label className="block text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">
              Intel Tags
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                placeholder="Add tags..."
                className="flex-1 px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-600"
              />
              <button
                onClick={addTag}
                className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2">
                    #{tag}
                    <button onClick={() => setTags(tags.filter((_, i) => i !== idx))} className="hover:text-white">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-emerald-500/30 bg-slate-900/50 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Abort Mission
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || !title}
            className="px-6 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-emerald-500/25"
            data-testid="intel-submit-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {uploading ? 'Transmitting...' : 'Log Intel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= INTEL VIEWER MODAL =================

const IntelViewerModal = ({ intel, onClose }) => {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  
  if (!intel) return null;
  
  const activeMedia = intel.media?.[activeMediaIndex];
  
  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/95"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-5xl rounded-xl bg-slate-950 border border-emerald-500/30 shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 via-emerald-950/20 to-slate-900 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{intel.title}</h3>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{intel.zone}</span>
                <span>•</span>
                <span>{formatDate(intel.created_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThreatBadge level={intel.threat_level} />
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Media Display */}
        {intel.media && intel.media.length > 0 && (
          <div className="flex-1 bg-black flex items-center justify-center relative min-h-96">
            {activeMedia?.type === 'video' ? (
              <video
                src={activeMedia.url}
                controls
                className="max-w-full max-h-full"
              />
            ) : activeMedia?.type === 'photo' || activeMedia?.type === 'image' ? (
              <img
                src={activeMedia.url}
                alt={intel.title}
                className="max-w-full max-h-full object-contain"
              />
            ) : activeMedia?.type === 'audio' ? (
              <div className="p-8 rounded-xl bg-slate-900 flex flex-col items-center gap-4">
                <Volume2 className="h-16 w-16 text-amber-400" />
                <audio src={activeMedia.url} controls />
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <FileText className="h-16 w-16 mx-auto mb-4" />
                <p>{activeMedia?.name || 'No media'}</p>
              </div>
            )}
            
            {/* Media Navigation */}
            {intel.media.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 rounded-lg bg-black/80 backdrop-blur-sm">
                {intel.media.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveMediaIndex(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === activeMediaIndex ? 'bg-emerald-500' : 'bg-slate-600 hover:bg-slate-500'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Details */}
        <div className="p-6 border-t border-slate-800 grid grid-cols-3 gap-6">
          <div>
            <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Condition</h4>
            <ConditionMeter rating={intel.condition_rating} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">GPS Coordinates</h4>
            {intel.gps ? (
              <p className="text-white font-mono text-sm">{intel.gps.lat}, {intel.gps.lng}</p>
            ) : (
              <p className="text-slate-500 text-sm">Not available</p>
            )}
          </div>
          <div>
            <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Issues Logged</h4>
            <p className="text-2xl font-bold text-white">{intel.issues?.length || 0}</p>
          </div>
        </div>
        
        {/* Description & Issues */}
        {(intel.description || intel.issues?.length > 0) && (
          <div className="px-6 pb-6 space-y-4">
            {intel.description && (
              <div>
                <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Situation Report</h4>
                <p className="text-slate-300">{intel.description}</p>
              </div>
            )}
            {intel.issues && intel.issues.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Identified Deficiencies</h4>
                <div className="space-y-2">
                  {intel.issues.map((issue, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-900 border border-slate-800">
                      <ThreatBadge level={issue.severity} />
                      <span className="text-white">{issue.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ================= MAIN COMPONENT =================

const FieldIntelTab = ({ projectId }) => {
  const [intel, setIntel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIntelModal, setNewIntelModal] = useState(false);
  const [viewIntel, setViewIntel] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const loadIntel = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const res = await axios.get(
        `${API_BASE}/api/projects/${projectId}/field-intel`,
        { withCredentials: true }
      );
      setIntel(res.data.intel || []);
    } catch (error) {
      console.error("Error loading intel:", error);
      // Load from local storage as fallback
      const stored = localStorage.getItem(`field_intel_${projectId}`);
      if (stored) {
        setIntel(JSON.parse(stored));
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    loadIntel();
  }, [loadIntel]);
  
  // Stats
  const stats = {
    total: intel.length,
    critical: intel.filter(i => i.threat_level === 'critical').length,
    high: intel.filter(i => i.threat_level === 'high').length,
    issues: intel.reduce((sum, i) => sum + (i.issues?.length || 0), 0),
    videos: intel.filter(i => i.media?.some(m => m.type === 'video')).length,
    photos: intel.filter(i => i.media?.some(m => m.type === 'photo')).length
  };
  
  // Filter intel
  const filteredIntel = intel.filter(i => {
    if (statusFilter !== 'all' && i.threat_level !== statusFilter) return false;
    if (zoneFilter !== 'all' && i.zone !== zoneFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return i.title?.toLowerCase().includes(q) || 
             i.description?.toLowerCase().includes(q) ||
             i.tags?.some(t => t.toLowerCase().includes(q));
    }
    return true;
  });
  
  const handleDelete = async (intelItem) => {
    if (!window.confirm('Delete this intel record?')) return;
    
    try {
      await axios.delete(
        `${API_BASE}/api/projects/${projectId}/field-intel/${intelItem.intel_id}`,
        { withCredentials: true }
      );
      setIntel(intel.filter(i => i.intel_id !== intelItem.intel_id));
      toast.success("Intel deleted");
    } catch {
      // Remove locally
      setIntel(intel.filter(i => i.intel_id !== intelItem.intel_id));
      toast.success("Intel deleted");
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="relative">
            <Radar className="h-16 w-16 text-emerald-500 mx-auto animate-spin" style={{ animationDuration: '3s' }} />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-4 h-4 bg-emerald-500 rounded-full animate-ping" />
            </span>
          </div>
          <p className="text-emerald-400 mt-4 font-mono text-sm">SCANNING FIELD DATA...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="field-intel-tab">
      {/* Command Header */}
      <div className="bg-gradient-to-r from-slate-900 via-emerald-950/30 to-slate-900 rounded-xl border border-emerald-500/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center relative">
              <Shield className="h-7 w-7 text-emerald-400" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-black">
                {stats.total}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-wide">FIELD INTELLIGENCE</h2>
              <p className="text-emerald-400/70 text-sm font-mono">RECON • DOCUMENT • REPORT</p>
            </div>
          </div>
          <button
            onClick={() => setNewIntelModal(true)}
            className="flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold transition-all shadow-lg shadow-emerald-500/25"
            data-testid="new-intel-btn"
          >
            <Crosshair className="h-5 w-5" />
            NEW INTEL
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4 mt-6">
          <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-700">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-xl font-bold text-white">{stats.total}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Total Logs</p>
          </div>
          <div className="p-3 rounded-lg bg-red-950/30 border border-red-500/30">
            <div className="flex items-center gap-2">
              <Skull className="h-4 w-4 text-red-400" />
              <span className="text-xl font-bold text-red-400">{stats.critical}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Critical</p>
          </div>
          <div className="p-3 rounded-lg bg-orange-950/30 border border-orange-500/30">
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-400" />
              <span className="text-xl font-bold text-orange-400">{stats.high}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">High Priority</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-950/30 border border-amber-500/30">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="text-xl font-bold text-amber-400">{stats.issues}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Issues</p>
          </div>
          <div className="p-3 rounded-lg bg-purple-950/30 border border-purple-500/30">
            <div className="flex items-center gap-2">
              <Film className="h-4 w-4 text-purple-400" />
              <span className="text-xl font-bold text-purple-400">{stats.videos}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Videos</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-950/30 border border-blue-500/30">
            <div className="flex items-center gap-2">
              <Image className="h-4 w-4 text-blue-400" />
              <span className="text-xl font-bold text-blue-400">{stats.photos}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Photos</p>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4 bg-slate-900/50 rounded-xl border border-slate-800 p-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search intel by title, description, or tags..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
            />
          </div>
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
        >
          <option value="all">All Threats</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="info">Info</option>
        </select>
        
        <select
          value={zoneFilter}
          onChange={(e) => setZoneFilter(e.target.value)}
          className="px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
        >
          <option value="all">All Zones</option>
          {ZONE_TYPES.map(z => (
            <option key={z.id} value={z.id}>{z.label}</option>
          ))}
        </select>
        
        <div className="flex gap-1 p-1 rounded-lg bg-slate-800 border border-slate-700">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
        
        <button
          onClick={loadIntel}
          className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-all"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      
      {/* Intel Grid/List */}
      {filteredIntel.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/50 rounded-xl border border-slate-800">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
            <Radar className="h-10 w-10 text-slate-600" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Intel Logged</h3>
          <p className="text-slate-500 mb-6">Begin reconnaissance to capture field intelligence</p>
          <button
            onClick={() => setNewIntelModal(true)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all"
          >
            <Crosshair className="h-5 w-5" />
            Start Recon
          </button>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
          {filteredIntel.map((item) => (
            <IntelCard
              key={item.intel_id}
              intel={item}
              onView={setViewIntel}
              onEdit={() => {}}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
      
      {/* Modals */}
      {newIntelModal && (
        <NewIntelModal
          projectId={projectId}
          onClose={() => setNewIntelModal(false)}
          onCreated={() => {
            setNewIntelModal(false);
            loadIntel();
          }}
        />
      )}
      
      {viewIntel && (
        <IntelViewerModal
          intel={viewIntel}
          onClose={() => setViewIntel(null)}
        />
      )}
    </div>
  );
};

export default FieldIntelTab;
